import { isEmpty } from 'lodash';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { notNil } from '@shared/util/notNil';
import { localizeCardSystemMessageContent } from './systemMessageCardUtils';

export interface SystemMessageInput {
  subType?: API.MessageSubType | null;
  content: string;
  sender: { username: string };
  meta: {
    channelName?: string | null;
    users?: Array<{ username: string } | null> | null;
  };
}

const listFormatter = new Intl.ListFormat(undefined, { type: 'conjunction' });

export function toString(message: SystemMessageInput): string {
  const users = message.meta?.users?.filter(notNil) ?? [];
  const userNames = users.map(u => u.username);

  if (message.subType === 'CHANNEL_CREATED') {
    return i18n('chat.systemMessage.channelCreated', { username: message.sender.username });
  }

  if (message.subType === 'MESSAGE_DELETED' || message.subType === 'COMMENT_DELETED') {
    return i18n('chat.systemMessage.messageRemoved');
  }

  if (message.subType === 'USER_JOINED') {
    return i18n('chat.systemMessage.userJoined', { username: message.sender.username });
  }

  if (message.subType === 'USERS_ADDED') {
    return i18n('chat.systemMessage.usersAdded', {
      users: userNames.join(', '),
      username: message.sender.username,
    });
  }

  if (message.subType === 'USER_REMOVED_FROM_CHANNEL') {
    return i18n('chat.systemMessage.usersRemoved', {
      users: userNames.join(', '),
      username: message.sender.username,
    });
  }

  if (message.subType === 'ME_REMOVED_FROM_CHANNEL') {
    return i18n('chat.systemMessage.removedFromChannel', {
      channelName: message.meta.channelName ?? '',
      byUser: userNames[0] ?? '',
    });
  }

  if (message.subType === 'USER_NOTIFIED' || message.subType === 'USERS_TEXTED') {
    // meta.users appears to include the sender as the last entry; strip it
    const recipients = userNames.slice(0, -1);

    if (isEmpty(recipients)) {
      return i18n('chat.sentBySms.noRecipients');
    }

    let items: string[];
    if (recipients.length <= 3) {
      items = recipients;
    } else {
      const numOther = recipients.length - 2;
      items = [recipients[0], recipients[1], i18n('common.nOthers', { count: numOther })];
    }

    return i18n('chat.sentBySms.withRecipients', { list: listFormatter.format(items) });
  }

  if (message.subType?.startsWith('THREAD_') || message.subType?.startsWith('CARD_')) {
    return localizeCardSystemMessageContent(message);
  }

  return '';
}
